import { Alert } from '@fx-ui/fine-design';
import { getApi, getCurrentLocaleKey, getSiteRouter, map, post, redirect } from '~/utils';
import { API, SITE_ROUTER } from '~/constants';
import Intl, { LOCALE_KEY } from '@fx-ui/intl';
import { Template, TemplateDetail } from '~/interfaces';

/**
 * 处理应用删除导致的模板安装失败(errorCode: 2004)
 */
export const handleTemplateRemoved = () => {
    Alert.openAlert({
        type: 'error',
        message: Intl.get('template.install.failed.back'),
        title: Intl.get('template.install.failed'),
        text4Cancel: Intl.get('template.install.back.dashboard'),
        text4Ok: Intl.get('template.install.back.center'),
        onCancel: () => {
            redirect(getSiteRouter(SITE_ROUTER.DASHBOARD));
        },
        onOk: () => {
            redirect(getSiteRouter(SITE_ROUTER.SOLUTION_CENTER));
        }
    });
};

export const fetchSolutionItem = (spaceId: string, dataId: string) => {
    return post<TemplateDetail>(getApi(API.help.solution_view), {
        space_id: spaceId,
        data_id: dataId
    }).then((res) => {
        const { data, similar } = res.data ?? {};
        return {
            data: transformTemplate(data),
            similar: map(similar, transformTemplate)
        };
    });
};

/**
 * 获取推荐模板列表
 * @param params 参数
 */
export const fetchRecommendTemplates = (params: {
    space_id: string;
    limit: number;
    skip: number;
}) => {
    return post<{ list: Template[] }>(getApi(API.help.solution_recommend), params).then(
        ({ data }) => {
            return map(data.list, transformTemplate);
        }
    );
};

/**
 * 搜索模板
 * @param params 搜索参数
 */
export const searchTemplates = (params: {
    space_id: string;
    limit: number;
    skip: number;
    keyword?: string;
    property?: Partial<Record<string, string>>;
    sort_by?: string;
}) => {
    return post<{ list: Template[]; count: number }>(getApi(API.help.find_solution), params).then(
        ({ data }) => {
            const { count, list = [] } = data ?? {};
            return {
                count,
                list: map(list, transformTemplate)
            };
        }
    );
};

/**
 * 获取模板列表
 * @param params 参数
 */
export const fetchTemplates = (params: { space_id: string; limit: number; skip: number }) => {
    return post<{ list: Template[]; count: number }>(getApi(API.help.solution_list), params).then(
        ({ data }) => {
            const { count, list = [] } = data ?? {};
            return {
                count,
                list: map(list, transformTemplate)
            };
        }
    );
};

/**
 * 业务侧转化模板结构以兼容template-sdk
 * @param template 原始模板信息
 */
export const transformTemplate = (template: Template) => {
    return {
        ...template,
        value: {
            ...template.value,
            price: template.value.fee_type
        }
    };
};

/**
 * 是否为付费模板
 */
export const isPaidTemplate = (template?: Template): template is Template => {
    return template?.value.fee_type === 'paid';
};

/**
 * 模板中心首页导航栏语言筛选默认跟随当前用户选择的语言
 * 如果不是繁中或者英文，则默认选中英文
 */
export const getDefaultTemplateLang = (): LOCALE_KEY => {
    const currentLocale = getCurrentLocaleKey();
    if ([LOCALE_KEY.ZH_TW, LOCALE_KEY.EN_US].includes(currentLocale)) {
        return currentLocale;
    }

    return LOCALE_KEY.EN_US;
};
